import axios from 'axios'
import jwtDefaultConfig from './jwtDefaultConfig'

const showError = (error) => toast.error(error, { duration: 5000, position: 'top-right', style: { background: '#f44336', color: '#fff' } })
export default class JwtService {
  // ** jwtConfig <= Will be used by this service
  jwtConfig = {...jwtDefaultConfig}
  #error = false
  // ** For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // ** For Refreshing Token
  subscribers = []

  constructor(jwtOverrideConfig) {
    this.jwtConfig = {...this.jwtConfig, ...jwtOverrideConfig}

    // ** Request Interceptor
    axios.interceptors.request.use(
      (config) => {
        // ** Get token from localStorage
        const accessToken = this.getToken()

        // ** If token is present add it to request's Authorization Header
        if (accessToken) {
          // ** eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // ** Add request/response interceptor
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // ** const { config, response: { status } } = error
        const {config, response} = error
        const originalRequest = config

        // ** if (status === 401) {
        if (response && response.status === 401) {
          showError('Su sesión ha expirado, por favor inicie sesión nuevamente')
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            this.refreshToken().then((r) => {
              this.isAlreadyFetchingAccessToken = false

              // ** Update accessToken in localStorage
              this.setToken(r.data.accessToken)
              this.setRefreshToken(r.data.refreshToken)

              this.onAccessTokenFetched(r.data.accessToken)
            })
          }
          return new Promise((resolve) => {
            this.addSubscriber((accessToken) => {
              // ** Make sure to assign accessToken according to your response.
              // ** Check: https://pixinvent.ticksy.com/ticket/2413870
              // ** Change Authorization header
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(this.axios(originalRequest))
            })
          })
        }

        // ** si hay un error por el usuario como ERR_INTERNET_DISCONNECTED
        if (!this.#error && response && response.status === 0) {
          this.#error = true
          showError('Error de conexión, por favor verifique su conexión a internet')
        }
        // ** si hay error 500
        if (!this.#error && response && response.status === 500) {
          this.#error = true
          showError('Error interno del servidor, por favor intente más tarde')
        }

        return Promise.reject(error)
      }
    )
    this.#clearError()
  }

  #clearError () {
    // ** Limpiamos el error cada 15 segundos.
    setInterval(() => {
      this.#error = false
    }, 10000)
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter((callback) =>
      callback(accessToken)
    )
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  login(...args) {
    return axios.post(this.jwtConfig.loginEndpoint, ...args)
  }

  getPasswordStatus(username) {
    return axios.get(`${this.jwtConfig.getPasswordStatus}/${username}`)
  }

  register(...args) {
    return axios.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return axios.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    })
  }

  isRequiredChangePassword(args) {
    return axios.post(`${this.jwtConfig.userEndpoint}/getSecurityUser`, args)
  }

  changePassword(args) {
    return axios.post(`${this.jwtConfig.userEndpoint}/resetPassword`, args)
  }

  // ! GLOBALCONFIG
  getData(params) {
    const {category, method} = params
    return axios.get(`${this.jwtConfig.apiEndpoint}/${category}/${method}`)
  }

  catalogMunicipios(departmentCode) {
    return axios.get(`${this.jwtConfig.employeeEndpoint}/municipios/${departmentCode}`)
  }

  // ! LOTS
  getBatchChildById(id) {
    return axios.get(`${this.jwtConfig.batchChildEndpint}/${id}`)
  }

  // * Buscamos un lote por ID y nos retornara el subloteID para insertar el empleado
  getBatchChildId(batchID) {
    return axios.get(`${this.jwtConfig.batchEndpint}/${batchID}`)
  }

  editBatchByID(data) {
    return axios.put(`${this.jwtConfig.batchEndpint}?${data}`)
  }

  getAllBatches() {
    return axios.get(`${this.jwtConfig.batchEndpint}/batches`)
  }

  approvaldBatch(selectedBatchId) {
    const payload = new FormData()
    payload.append('Approved', true)
    payload.append('id', selectedBatchId)
    return axios.put(
      `${this.jwtConfig.batchEndpint}/${selectedBatchId}`,
      payload
    )
  }

  uploadExcel(data) {
    // console.log('data', data)
    const {fileData, fileName, description} = data
    const payload = new FormData()
    payload.append('fileName', fileName)
    payload.append('source', 'file')
    payload.append('fileData', fileData)
    payload.append('description', description)
    return axios.post(`${this.jwtConfig.batchEndpint}/save/base64`, payload)
  }

  // ! EMPLOYEE
  getEmployeeById(id) {
    return axios.get(`${this.jwtConfig.employeeEndpoint}/${id}`)
  }

  addEmployee(data) {
    return axios.post(this.jwtConfig.employeeEndpoint, data)
  }

  editEmployee(data) {
    return axios.put(this.jwtConfig.employeeEndpoint, data)
  }

  //! Catalog
  getCatalog(catalog) {
    return axios.get(`${this.jwtConfig.catalogEndpoint}/${catalog}`)
  }
}
